import React, { useEffect } from "react";
import { authorization, currentUser, legit } from "apex-legit-service";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";

import { ALL_REQUESTED_PERMISSIONS, LEGIT_SERVICE_CONFIG } from "common";
import { RootAction, RootState } from "StoreTypes";
import ApexFooter from "components/ApexFooter";
import ApexHeader from "components/ApexHeader";
import { FormattedMessage } from "react-intl";
import { config, isActiveLink, links } from "utils";
import { Redirect, Route, Switch } from "react-router-dom";
import messages from "./messages";
import Home from "../Home";
import File from "../File";
import { useFileSearch } from "../../hooks";
import ApexLogin from "../ApexLogin";
import { apiToken } from "../../services/legit/token";
import { HTTPStatusCodes } from "../../types";

interface Props {
  token: string | null;
  logout: (config: Record<string, any>, params: { apexUser: boolean }) => void;
  showLogin: boolean;
  showAppContent: boolean;
  adoptLegitSession: (
    config: Record<string, any>,
    requestedPermissions: string[]
  ) => void;
}

const App = ({
  token,
  logout,
  showLogin,
  showAppContent,
  adoptLegitSession,
}: Props) => {
  const { fileState } = useFileSearch();
  const authToken = token as string;

  useEffect(() => {
    adoptLegitSession(LEGIT_SERVICE_CONFIG, ALL_REQUESTED_PERMISSIONS);
  }, [adoptLegitSession]);

  useEffect(() => {
    config.headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };
  }, [window.location.pathname]);

  function handleLogout() {
    const params = { apexUser: false };
    logout(LEGIT_SERVICE_CONFIG, params);
  }

  const filesRoute = () => {
    return fileState.status === HTTPStatusCodes.STATUS_REDIRECT ? (
      <Route path="/filestorage/applications">
        <File isPublic />
      </Route>
    ) : (
      <ApexLogin />
    );
  };

  return (
    <div>
      <ApexHeader
        title={<FormattedMessage {...messages.title} />}
        links={links}
        isActiveLink={isActiveLink}
        onLogoutClick={handleLogout}
        showLogout={!showLogin}
      />
      <Switch>
        <Route exact path="/" render={() => <Redirect to="/filestorage" />} />
        {showAppContent ? (
          <>
            <Route exact path="/filestorage" component={Home} />
            <Route path="/filestorage/applications">
              <File isPublic={false} />
            </Route>
          </>
        ) : (
          filesRoute()
        )}
      </Switch>
      <ApexFooter />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  token: apiToken(state),
  permissions: authorization.selectors.selectLegitPermissions(state),
  showLogin: legit.selectors.selectShowLoginUI(state) === legit.LOGIN_UI_SHOW,
  showLogout: legit.selectors.selectShowLogoutUI(state),
  showAppContent:
    legit.selectors.selectShowLoginUI(state) === legit.LOGIN_UI_HIDE,
  orgUnits: currentUser.selectors.selectResults(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) =>
  bindActionCreators(
    {
      adoptLegitSession: legit.adoptExistingSession,
      login: legit.login,
      logout: () => legit.logout(LEGIT_SERVICE_CONFIG, { apexUser: true }),
    },
    dispatch
  );

export { default as sagas } from "./sagas";

export default connect(mapStateToProps, mapDispatchToProps)(App);
